import { APITypes } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getUserPreferencesCacheKey, onPreferencesUpdate } from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useNotify from '../useNotify';

const intl = getIntl('useMyPreferences', {
    errorLoadingPreferences: 'Error loading preferences',
    errorUpdatingPreferences: 'Error updating preferences',
    preferencesUpdated: 'Preferences updated',
});

export type UseMyPreferencesArgs = {
    onUpdatePreferencesSuccess?: () => void;
};

export type UseMyPreferencesReturnValue = {
    isLoading: boolean;
    preferences: APITypes.userService.GetUserPreferencesResponse | null;
    updatePreferences: (update: APITypes.userService.UpsertUserPreferencesRequest) => void;
    isUpdatingPreferences: boolean;
};

export default function useMyPreferences({
    onUpdatePreferencesSuccess,
}: UseMyPreferencesArgs = {}): UseMyPreferencesReturnValue {
    const notify = useNotify();
    const preferencesQuery = useQuery<APITypes.userService.GetUserPreferencesResponse>({
        queryKey: getUserPreferencesCacheKey(),
        queryFn: async () => {
            return await appAPI.get(urls.api.users.myPreferences);
        },
    });
    useQueryErrorNotify(preferencesQuery, intl('errorLoadingPreferences'));

    const updatePreferencesMutation = useMutation({
        mutationFn: async (args: APITypes.userService.UpsertUserPreferencesRequest) => {
            return await appAPI.put(urls.api.users.myPreferences, {
                body: args,
            });
        },
        onError: () => {
            notify('error', intl('errorUpdatingPreferences'));
        },
        onSuccess: () => {
            notify('success', intl('preferencesUpdated'));
            onUpdatePreferencesSuccess?.();
            onPreferencesUpdate.resetCache({})();
        },
    });

    return {
        isLoading: preferencesQuery.isLoading,
        preferences: preferencesQuery.data || null,
        updatePreferences: updatePreferencesMutation.mutate,
        isUpdatingPreferences: updatePreferencesMutation.isPending,
    };
}
